import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Download, X, Check, AlertTriangle } from "lucide-react";
import { api, API } from "@/lib/api";
import SignaturePad from "@/components/SignaturePad";

/** ConsentStatusChip — small status pill reused across admin views. */
export function ConsentStatusChip({ status, testid }) {
  const map = {
    not_started: {
      cls: "border-red-300 bg-red-50 text-red-800",
      label: "⚠ NOT STARTED",
      title: "Client hasn't opened or completed the consent form",
    },
    submitted_awaiting_review: {
      cls: "border-amber-400 bg-amber-100 text-amber-900",
      label: "🟡 AWAITING REVIEW",
      title: "Client submitted their consent form — awaiting practitioner review",
    },
    reviewed_signed: {
      cls: "border-emerald-300 bg-emerald-50 text-emerald-800",
      label: "✅ REVIEWED & SIGNED",
      title: "Practitioner has reviewed and signed the consent form",
    },
  };
  const cfg = map[status] || map.not_started;
  return (
    <span
      data-testid={testid}
      title={cfg.title}
      className={`mono-label inline-block px-2 py-1 border text-[10px] whitespace-nowrap ${cfg.cls}`}
    >
      {cfg.label}
    </span>
  );
}

/**
 * ConsentReviewDrawer — opens on top of any admin drawer to show the full
 * client submission and allow the practitioner to review-and-sign.
 *
 * Props:
 *   formId       — id of the consent_forms document
 *   onClose      — close handler
 *   onSigned     — called after successful review-sign (parent refreshes)
 */
export default function ConsentReviewDrawer({ formId, onClose, onSigned }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [hasReviewed, setHasReviewed] = useState(false);
  const [pracName, setPracName] = useState("");
  const [pracSig, setPracSig] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const padRef = useRef(null);

  useEffect(() => {
    api.get(`/consent-forms/${formId}`)
      .then((r) => setData(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || "Could not load consent form"));
  }, [formId]);

  const downloadPdf = async () => {
    const token = localStorage.getItem("clae_token");
    const r = await fetch(`${API}/consent-forms/${formId}/pdf`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!r.ok) {
      const t = await r.text();
      toast.error(t.includes("must be reviewed") ? "Sign the form first to export as PDF" : "Could not download PDF");
      return;
    }
    const blob = await r.blob();
    const u = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = u;
    a.download = `clae-consent-${data.booking.reference}-v${data.form.form_version || 1}.pdf`;
    a.click();
    URL.revokeObjectURL(u);
  };

  const submitSign = async () => {
    if (!hasReviewed) return toast.error("Please tick 'I have reviewed…' first");
    if (!pracName.trim()) return toast.error("Type your full name");
    if (!pracSig) return toast.error("Sign in the box");
    setSubmitting(true);
    try {
      await api.post(`/consent-forms/${formId}/review-sign`, {
        has_reviewed: true,
        practitioner_name: pracName.trim(),
        practitioner_signature: pracSig,
      });
      toast.success("Consent reviewed & signed");
      onSigned && onSigned();
      onClose && onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not sign consent");
    }
    setSubmitting(false);
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-stone-900/50 backdrop-blur-sm"
      onClick={onClose}
      data-testid="consent-review-drawer"
    >
      <div
        className="absolute right-0 top-0 h-full w-full sm:max-w-2xl bg-white overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-white border-b hairline p-6 flex items-center justify-between z-10">
          <div>
            <p className="mono-label text-stone-400 text-[10px]">CONSENT FORM · REVIEW</p>
            <h3 className="font-serif text-2xl mt-1">
              {data?.form?.treatment_name || (err ? "Error" : "Loading…")}
            </h3>
          </div>
          <button
            data-testid="close-consent-review"
            onClick={onClose}
            className="p-2 hover:bg-alt border hairline"
          >
            <X size={16} />
          </button>
        </div>

        {err && <p className="p-6 text-sm text-red-700">{err}</p>}
        {!data && !err && <p className="p-6 mono-label">Loading…</p>}

        {data && <ReviewBody
          data={data}
          hasReviewed={hasReviewed}
          setHasReviewed={setHasReviewed}
          pracName={pracName}
          setPracName={setPracName}
          setPracSig={setPracSig}
          pracSig={pracSig}
          padRef={padRef}
          submitting={submitting}
          submitSign={submitSign}
          downloadPdf={downloadPdf}
        />}
      </div>
    </div>
  );
}

function ReviewBody({
  data, hasReviewed, setHasReviewed, pracName, setPracName,
  pracSig, setPracSig, padRef, submitting, submitSign, downloadPdf,
}) {
  const { form, template, client, booking, versions } = data;
  const isSigned = form.status === "reviewed_signed";
  const q_map = Object.fromEntries((template.questions || []).map((q) => [q.key, q]));
  return (
    <div className="p-6 sm:p-8 space-y-8">
      {/* Header meta */}
      <section>
        <div className="flex items-center gap-3 flex-wrap">
          <ConsentStatusChip status={form.status} testid="review-status-chip" />
          <span className="mono-label text-stone-500">Version {form.form_version || 1}</span>
          {versions && versions.length > 1 && (
            <span className="mono-label text-stone-400 text-[10px]">
              · {versions.length} versions on file
            </span>
          )}
        </div>
        <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
          <div>
            <p className="mono-label text-stone-400 text-[10px]">CLIENT</p>
            <p className="font-medium mt-1">{client.first_name} {client.last_name}</p>
            <p className="text-stone-600">{client.email}</p>
            <p className="text-stone-600">{client.phone}</p>
            {client.dob && <p className="text-stone-600">DOB · {client.dob}</p>}
            {(client.address || client.postcode) && (
              <p className="text-stone-600 text-xs mt-1">{client.address}, {client.postcode}</p>
            )}
          </div>
          <div>
            <p className="mono-label text-stone-400 text-[10px]">TREATMENT & BOOKING</p>
            <p className="font-medium mt-1">{form.treatment_name}</p>
            <p className="text-stone-600 font-mono text-xs">{booking.reference}</p>
            <p className="text-stone-600 text-xs">{booking.date} · {booking.time}</p>
            <p className="text-stone-600 text-xs mt-1">Template · {template.title || form.template_title}</p>
          </div>
        </div>
      </section>

      {/* Timestamps */}
      <section className="border-t hairline pt-4">
        <div className="grid grid-cols-2 gap-3 text-xs">
          <div>
            <p className="mono-label text-stone-400 text-[10px]">SUBMITTED BY CLIENT</p>
            <p className="mt-1 text-stone-800">
              {(form.submitted_at || form.signed_at || "").slice(0, 16).replace("T", " ")} UTC
            </p>
          </div>
          <div>
            <p className="mono-label text-stone-400 text-[10px]">REVIEWED & SIGNED</p>
            <p className="mt-1 text-stone-800">
              {form.reviewed_at
                ? `${(form.reviewed_at || "").slice(0, 16).replace("T", " ")} UTC · ${form.reviewed_by}`
                : "—"}
            </p>
          </div>
        </div>
      </section>

      {/* Answers */}
      <section className="border-t hairline pt-6">
        <p className="mono-label">Client Responses ({form.answers?.length || 0})</p>
        <div className="mt-4 space-y-4">
          {(form.answers || []).map((a, i) => {
            const q = q_map[a.key] || { text: a.key };
            const answer = (a.answer || "").toUpperCase();
            const isConcerning = (q.refuse_on_yes && a.answer === "yes") ||
                                 (q.require_yes && a.answer === "no");
            return (
              <div
                key={i}
                className={`border-l-2 pl-4 ${isConcerning ? "border-amber-400 bg-amber-50/40" : "border-stone-200"}`}
                data-testid={`review-answer-${a.key}`}
              >
                <p className="text-sm text-stone-900">{q.text}</p>
                {q.kind !== "comment_only" && (
                  <p className="mt-1 text-xs">
                    <span className="mono-label text-stone-400">ANSWER · </span>
                    <span className={`font-semibold ${isConcerning ? "text-amber-800" : "text-stone-900"}`}>
                      {answer || "—"}
                    </span>
                    {isConcerning && (
                      <span className="ml-2 inline-flex items-center gap-1 text-amber-800">
                        <AlertTriangle size={12} /> Review this response
                      </span>
                    )}
                  </p>
                )}
                {a.comment && (
                  <p className="mt-1 text-xs text-stone-700 italic whitespace-pre-line">
                    <span className="mono-label text-stone-400 not-italic">COMMENT · </span>{a.comment}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      </section>

      {/* Client signature */}
      <section className="border-t hairline pt-6">
        <p className="mono-label">Client Signature</p>
        {form.signature_image ? (
          <img
            src={form.signature_image}
            alt="Client signature"
            className="mt-3 max-h-40 border hairline bg-white p-2"
            data-testid="client-signature-image"
          />
        ) : (
          <p className="mt-3 text-sm text-stone-500">No signature captured.</p>
        )}
        <p className="mono-label text-stone-400 mt-2 text-[10px]">
          — {form.signature_name || `${client.first_name} ${client.last_name}`}
        </p>
      </section>

      {/* Practitioner review & sign — only if not yet signed */}
      {!isSigned ? (
        <section className="border-t hairline pt-6" data-testid="practitioner-sign-section">
          <p className="mono-label">Practitioner Review &amp; Signature</p>
          <p className="text-xs text-stone-600 mt-2">
            Confirm you've read every response above. Once signed, this consent becomes the
            official record for this treatment and cannot be edited.
          </p>

          <label className="mt-4 flex items-start gap-3 text-sm cursor-pointer">
            <input
              type="checkbox"
              className="mt-1"
              checked={hasReviewed}
              onChange={(e) => setHasReviewed(e.target.checked)}
              data-testid="has-reviewed-checkbox"
            />
            <span>
              I have reviewed all of the client's questions and answers, including any comments
              and safety-relevant flags.
            </span>
          </label>

          <div className="mt-4">
            <label className="mono-label text-stone-500">Practitioner Full Name</label>
            <input
              type="text"
              value={pracName}
              onChange={(e) => setPracName(e.target.value)}
              placeholder="Type your full name"
              className="mt-1 w-full border hairline p-2 text-sm focus:outline-none focus:border-stone-900"
              data-testid="practitioner-name-input"
            />
          </div>

          <div className="mt-4">
            <div className="flex items-baseline justify-between">
              <label className="mono-label text-stone-500">Practitioner Signature</label>
              <button
                type="button"
                data-testid="clear-practitioner-sig"
                onClick={() => padRef.current?.clear()}
                className="mono-label text-stone-500 hover:text-stone-900"
              >
                Clear
              </button>
            </div>
            <div className="mt-2">
              <SignaturePad
                ref={padRef}
                height={150}
                onChange={(url) => setPracSig(url)}
              />
            </div>
            <p className="mono-label text-stone-400 mt-1 text-[10px]">
              {pracSig ? "Signed" : "Awaiting signature"}
            </p>
          </div>

          <button
            data-testid="review-sign-submit"
            onClick={submitSign}
            disabled={submitting || !hasReviewed || !pracName.trim() || !pracSig}
            className="mt-6 w-full mono-label bg-stone-900 text-white py-4 disabled:opacity-30 hover:bg-[#C5A059] transition"
            style={{ color: "#fff" }}
          >
            {submitting ? "Signing…" : "Review & Sign Consent"}
          </button>
        </section>
      ) : (
        <section className="border-t hairline pt-6" data-testid="already-signed-section">
          <p className="mono-label text-emerald-700">Signed by Practitioner</p>
          {form.practitioner_signature_image ? (
            <img
              src={form.practitioner_signature_image}
              alt="Practitioner signature"
              className="mt-3 max-h-40 border hairline bg-white p-2"
              data-testid="practitioner-signature-image"
            />
          ) : (
            <p className="mt-3 text-sm text-stone-500 italic font-serif">
              {form.practitioner_signature_name || form.reviewed_by}
            </p>
          )}
          <p className="mono-label text-stone-400 mt-2 text-[10px]">
            — {form.practitioner_signature_name || form.reviewed_by} ·
            {" "}{(form.reviewed_at || "").slice(0, 16).replace("T", " ")} UTC
          </p>
          <button
            data-testid="export-consent-pdf"
            onClick={downloadPdf}
            className="mt-6 w-full mono-label border border-stone-900 py-3 hover:bg-stone-900 hover:text-white flex items-center justify-center gap-2 transition"
          >
            <Download size={14} /> Export as PDF
          </button>
        </section>
      )}

      {/* Version history */}
      {versions && versions.length > 1 && (
        <section className="border-t hairline pt-6">
          <p className="mono-label">Version History</p>
          <ul className="mt-3 space-y-1 text-xs">
            {versions.map((v) => (
              <li key={v.id} className={`flex items-center justify-between py-1 ${v.id === form.id ? "font-semibold" : "text-stone-600"}`}>
                <span>v{v.form_version} · {(v.submitted_at || "").slice(0, 10)}</span>
                <span className="flex items-center gap-2">
                  {v.status === "reviewed_signed" ? (
                    <span className="inline-flex items-center gap-1 text-emerald-700">
                      <Check size={12} /> Signed
                    </span>
                  ) : (
                    <span className="text-amber-700">Awaiting review</span>
                  )}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
